import type { ShareOutcome } from "@wags/ui/share-or-copy.ts";
import { toast } from "@wags/ui/sonner.tsx";
import { MOBILE_VIEWPORT_QUERY } from "@wags/tanstack-start/react";
import type { PostHogInterface } from "posthog-js";
import { useRef, useState } from "react";
import { getChatControllerState } from "@/features/kits-conversations";
import type { FavoriteMutationResult } from "@/features/saved-listings";
import {
    claimShareOffer,
    type ShareOfferProviderKind,
    useShareOfferEnabled,
} from "./share-offer";
import { ShareOfferMessage } from "./share-offer-message";

const CRAMPED_VIEWPORT_QUERY = "(max-height: 499px), (orientation: landscape)";
const TOAST_BACKSTOP_MS = 60_000;
const MESSAGE_MAX_HEIGHT_PX = 56;
const STICKY_MOBILE_HEADER_MAX_HEIGHT_PX = 125;

function skipReason(
    alreadyOffered: boolean,
    result: FavoriteMutationResult,
): string | null {
    if (alreadyOffered) return "already_offered";
    if (result.outcome === "guest-saved-sign-in-prompt")
        return "sign_in_prompt";
    if (getChatControllerState().chatOpen) return "kits_chat_open";
    return null;
}

function slotClearsStickyHeader(slot: HTMLElement | null): boolean {
    if (!slot) return false;
    return (
        slot.getBoundingClientRect().bottom - MESSAGE_MAX_HEIGHT_PX >=
        STICKY_MOBILE_HEADER_MAX_HEIGHT_PX
    );
}

export function useShareOfferAfterSave({
    posthog,
    isSignedIn,
    listingId,
    text,
    photoUrl,
    providerKind,
    onShare,
}: {
    posthog: Pick<PostHogInterface, "capture">;
    isSignedIn: boolean;
    listingId: string;
    text: string;
    photoUrl: string | undefined;
    providerKind: ShareOfferProviderKind;
    onShare: () => Promise<ShareOutcome>;
}) {
    const enabled = useShareOfferEnabled();
    const slotRef = useRef<HTMLDivElement>(null);
    const [offeredListingId, setOfferedListingId] = useState<string | null>(
        null,
    );
    if (offeredListingId !== null && offeredListingId !== listingId) {
        setOfferedListingId(null);
    }

    const renderMessage = (close: () => void) => (
        <ShareOfferMessage
            text={text}
            photoUrl={photoUrl}
            onShare={onShare}
            onClose={close}
            onExpire={() => {
                posthog.capture("share_prompt_expired", { dogId: listingId });
                close();
            }}
        />
    );

    const toastId = `share-offer-${listingId}`;
    const handleSaved = (result: FavoriteMutationResult) => {
        if (!enabled) return;
        const isFirstOffer = claimShareOffer(listingId);
        const reason = skipReason(!isFirstOffer, result);
        if (reason) {
            setOfferedListingId(null);
            toast.dismiss(toastId);
            posthog.capture("share_prompt_skipped", {
                reason,
                dogId: listingId,
            });
            return;
        }

        const surface =
            window.matchMedia(MOBILE_VIEWPORT_QUERY).matches &&
            !window.matchMedia(CRAMPED_VIEWPORT_QUERY).matches &&
            slotClearsStickyHeader(slotRef.current)
                ? "photo"
                : "toast";
        posthog.capture("share_prompt_shown", {
            trigger: "save",
            providerKind,
            surface,
            signedIn: isSignedIn,
            dogId: listingId,
        });
        if (surface === "photo") {
            setOfferedListingId(listingId);
            return;
        }

        toast.custom(
            () => (
                <div className="w-full md:w-95">
                    {renderMessage(() => toast.dismiss(toastId))}
                </div>
            ),
            { id: toastId, unstyled: true, duration: TOAST_BACKSTOP_MS },
        );
    };

    const handleUnsaved = () => {
        setOfferedListingId(null);
        toast.dismiss(toastId);
    };

    const message =
        offeredListingId === listingId
            ? renderMessage(() => setOfferedListingId(null))
            : null;

    return {
        handleSaved,
        handleUnsaved,
        message,
        slotRef,
    };
}
